import { Trans, useLingui } from '@lingui/react/macro';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { BaseModal } from '@/components/ui/base-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  generateErrorReport,
  generateTemplateFile,
  parseExcelFile,
  validateShiftData,
  type ParsedShiftData,
  type ValidationError,
} from '@/domain/templates/excel-import';
import type { ShiftSet, ShiftType, StaffGroup, Tag } from '@/types/template';

/**
 * Bulk shift-import modal (M3). User uploads an `.xlsx` file shaped
 * like the blank template (also downloadable from this modal). The
 * file is parsed and validated against the realm's shift types,
 * staff groups, and tags; the result is a split of valid rows ready
 * to add and a per-row error list.
 *
 * Three actions are surfaced once a file is processed:
 * - **Import valid rows** — fires `onImport(validShifts)` with the
 *   parsed shifts; enabled only when at least one row validates.
 * - **Download error report** — re-emits the user's file with an
 *   `Errors` column annotating what to fix; enabled only when
 *   validation produced errors.
 * - **Cancel** — closes the modal without importing anything.
 */
export function ImportShiftsModal({
  shiftTypes,
  staffGroups,
  tags,
  onImport,
  onCancel,
}: ImportShiftsModalProps) {
  const { t } = useLingui();
  const [parsedRows, setParsedRows] = useState<ParsedShiftData[]>([]);
  const [validShifts, setValidShifts] = useState<ShiftSet[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [busy, setBusy] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  /**
   * Parse the uploaded `.xlsx` and split the rows into valid shifts +
   * per-row validation errors. Surfaces a generic copy-friendly
   * message if the file can't be read at all (corrupt, wrong format).
   */
  async function handleFile(file: File) {
    setBusy(true);
    setParseError(null);
    try {
      const rows = await parseExcelFile(file);
      const { validShifts: valid, errors: rowErrors } = validateShiftData(
        rows,
        shiftTypes,
        staffGroups,
        tags,
      );
      setParsedRows(rows);
      setValidShifts(valid);
      setErrors(rowErrors);
    } catch {
      setParseError(t`Couldn't read the file. Check the format and try again.`);
      setParsedRows([]);
      setValidShifts([]);
      setErrors([]);
    } finally {
      setBusy(false);
    }
  }

  const hasResults = parsedRows.length > 0 || errors.length > 0;

  return (
    <BaseModal
      open
      onOpenChange={(open) => !open && onCancel()}
      title={<Trans>Import shifts from Excel</Trans>}
      description={
        <Trans>
          Upload a filled-in template to add multiple shifts at once. Download a blank template if
          you need one.
        </Trans>
      }
      className="sm:max-w-xl"
      footer={
        <div className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="outline"
            onClick={() => generateErrorReport(parsedRows, errors)}
            disabled={errors.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            <Trans>Download error report</Trans>
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={() => onImport(validShifts)} disabled={validShifts.length === 0}>
              <Trans>Import valid rows</Trans>
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => generateTemplateFile(shiftTypes, staffGroups, tags)}
        >
          <Download className="mr-2 h-4 w-4" />
          <Trans>Download blank template</Trans>
        </Button>

        <div className="space-y-2">
          <Label htmlFor="import-file">
            <Upload className="mr-1 inline h-4 w-4" />
            <Trans>Upload .xlsx file</Trans>
          </Label>
          <Input
            id="import-file"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
        </div>

        {parseError && <p className="text-destructive text-sm">{parseError}</p>}

        {hasResults && (
          <div className="bg-muted/30 rounded-md border p-3 text-sm">
            <p className="font-medium">
              <FileSpreadsheet className="mr-1 inline h-4 w-4" />
              {validShifts.length === 1 ? (
                <Trans>1 valid row</Trans>
              ) : (
                <Trans>{validShifts.length} valid rows</Trans>
              )}
              {' · '}
              {errors.length === 1 ? <Trans>1 error</Trans> : <Trans>{errors.length} errors</Trans>}
            </p>
          </div>
        )}
      </div>
    </BaseModal>
  );
}

interface ImportShiftsModalProps {
  shiftTypes: ShiftType[];
  staffGroups: StaffGroup[];
  tags: Tag[];
  onImport: (shifts: ShiftSet[]) => void;
  onCancel: () => void;
}
